import { DEVICE_TYPE } from "@/data/constants";
import { useEffect, useState } from "react";
import { UAParser } from "ua-parser-js";

type TDeviceType = "mobile" | "tablet" | "desktop";

interface DeviceInfo {
  deviceType: TDeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isMobileOrTablet: boolean;
}

let cachedParser: UAParser.IResult | null = null;

const detectDeviceType = (): TDeviceType => {
  if (typeof window === "undefined") return "desktop";

  try {
    if (!cachedParser) {
      const parser = new UAParser();
      cachedParser = parser.getResult();
    }

    const { device, os } = cachedParser;

    // If UAParser detected device type, use it
    if (device.type === "mobile") return "mobile";
    if (device.type === "tablet") return "tablet";

    // For mobile OS, use screen size to differentiate
    if (os.name === "iOS" || os.name === "Android") {
      return window.innerWidth >= 768 ? "tablet" : "mobile";
    }

    // Desktop OS always returns desktop (regardless of window size)
    const desktopOS = [
      "Windows",
      "macOS",
      "Mac OS",
      "Linux",
      "Ubuntu",
      "Debian",
      "Fedora",
      "Chrome OS",
    ];
    if (desktopOS.includes(os.name || "")) {
      return "desktop";
    }

    // Fallback to screen size
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  } catch {
    return "desktop";
  }
};

const useDeviceType = (): DeviceInfo => {
  const [deviceType, setDeviceType] = useState<TDeviceType>(() =>
    detectDeviceType(),
  );

  useEffect(() => {
    const handleResize = () => {
      setDeviceType(detectDeviceType());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    deviceType,
    isMobile: deviceType === DEVICE_TYPE.MOBILE,
    isTablet: deviceType === DEVICE_TYPE.TABLET,
    isDesktop: deviceType === DEVICE_TYPE.DESKTOP,
    isMobileOrTablet: deviceType !== DEVICE_TYPE.DESKTOP,
  };
};

// Export the detection function for non-hook usage ( Firebase)
export const getDeviceType = detectDeviceType;

export default useDeviceType;
